"use client";

import { useEffect } from "react";

/**
 * Measures the site header chrome (everything stacked above <main>)
 * and publishes it as the `--site-header-h` CSS variable on <html>.
 *
 * A full-height hero can then size itself to
 * `calc(100dvh - var(--site-header-h))` so the header + hero together
 * fill exactly one viewport instead of the hero being a full 100vh and
 * pushing the page past the screen edge.
 *
 * The header reflows a few px TALLER once the web font swaps in,
 * which lands a frame or two AFTER mount / `fonts.ready` / `load`. To
 * avoid locking in the stale (shorter) height we:
 *   - re-measure across several animation frames + delays, and
 *   - track the tallest value seen during settling (the font swap only
 *     grows the header), resetting that high-water mark only on a real
 *     window resize (where the header may legitimately shrink).
 */
export function HeaderHeightVar() {
  useEffect(() => {
    const main = document.querySelector("main");
    if (!main) return;

    let maxH = 0;

    const apply = (reset = false) => {
      if (reset) maxH = 0;
      const top = main.getBoundingClientRect().top + window.scrollY;
      maxH = Math.max(maxH, Math.max(0, Math.round(top)));
      document.documentElement.style.setProperty("--site-header-h", `${maxH}px`);
    };

    // Initial measure + a burst of re-measures spanning the font swap
    // and late header hydration (auth/user-menu can nudge it a few px
    // after ~1s).
    apply();
    requestAnimationFrame(() => apply());
    const timers = [50, 200, 500, 1000, 1800].map((ms) =>
      setTimeout(() => apply(), ms),
    );
    (document as any).fonts?.ready
      ?.then(() => requestAnimationFrame(() => apply()))
      .catch(() => {});

    const onResize = () => apply(true); // real resize → recompute fresh
    const onLoad = () => apply();
    window.addEventListener("resize", onResize);
    window.addEventListener("load", onLoad);

    // Observe the whole document body, not just <header> elements: the
    // chrome above <main> includes non-<header> wrappers (mobile bar,
    // ChromeGate) that can reflow on hydration. Any layout change above
    // main shifts main's top, which apply() re-reads.
    const ro = new ResizeObserver(() => apply());
    ro.observe(document.body);

    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("load", onLoad);
      ro.disconnect();
    };
  }, []);

  return null;
}

export default HeaderHeightVar;
